// ═══════════════════════════════════════════════════════════════════════
// Temps › Réglages › Bornes — enrôler une borne de pointage de site, lister les bornes, révoquer
// avec un motif. Droit : gerer_bornes (portée entreprise), vérifié par le serveur
// (/api/temps/pointage/droits, routes de l'agent C). La clé de l'appareil n'est montrée qu'UNE fois.
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Tablet, Plus, Ban, KeyRound, Copy, Check } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, Badge, BandeauInfo, EtatVide, Squelette, Selecteur } from '../../design/composants';
import { NavReglages, Fenetre, Champ, PiedFenetre } from './reglagesCommun';
import { useDroitsTemps, messageErreur, dateHeure } from './rhCommun';

const API = '/api/temps/pointage/bornes';

function FenetreEnroler({ sites, onFermer, onEnrolee }) {
  const [f, setF] = useState({ site_id: '', libelle: '' });
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const envoyer = async () => {
    setEnvoi(true); setErreur('');
    try { onEnrolee((await axios.post(API, f)).data.data); } catch (e) { setErreur(messageErreur(e)); setEnvoi(false); }
  };
  return (
    <Fenetre titre="Enrôler une borne" Icone={Plus} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <Champ libelle="Site" requis>
        <select className="rh-champ-saisie" value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })}>
          <option value="">Choisir…</option>
          {sites.map((s) => <option key={s.id} value={s.id}>{s.libelle}</option>)}
        </select>
      </Champ>
      <Champ libelle="Nom de la borne" requis aide="Où elle se trouve, par exemple « Entrée principale ».">
        <input className="rh-champ-saisie" value={f.libelle} maxLength={120} onChange={(e) => setF({ ...f, libelle: e.target.value })} />
      </Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="primaire" Icone={Plus} onClick={envoyer} disabled={envoi || !f.site_id || f.libelle.trim().length < 3}>Enrôler</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

/** La clé n'est affichée qu'ici, une seule fois. */
function FenetreCle({ resultat, onFermer }) {
  const [copie, setCopie] = useState(false);
  const [lu, setLu] = useState(false);
  const copier = async () => { try { await navigator.clipboard.writeText(resultat.cle); setCopie(true); } catch (_) { setCopie(false); } };
  return (
    <Fenetre titre={`Clé de la borne « ${resultat.borne.libelle} »`} Icone={KeyRound} onFermer={() => { if (lu) onFermer(); }}>
      <BandeauInfo ton="alerte" titre="Cette clé ne sera plus jamais affichée">
        {resultat.avertissement || 'Recopiez-la maintenant sur la borne. Si elle est perdue, révoquez la borne et enrôlez-en une nouvelle.'}
      </BandeauInfo>
      <ol style={{ margin: '0 0 12px', paddingLeft: 18, fontSize: 13 }}>
        <li>Sur la tablette de la borne, ouvrez la page « Borne » de SOGAS RH.</li>
        <li>Collez ou saisissez la clé ci-dessous, puis enregistrez.</li>
        <li>Ne l'envoyez ni par message ni par courriel ; ne la notez pas sur papier.</li>
      </ol>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <code style={{ fontSize: 15, padding: '8px 10px', background: 'var(--sg-surface-2)', borderRadius: 'var(--sg-rayon)', wordBreak: 'break-all', flex: 1 }}>{resultat.cle}</code>
        <Bouton Icone={copie ? Check : Copy} onClick={copier}>{copie ? 'Copiée' : 'Copier'}</Bouton>
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
        <input type="checkbox" checked={lu} onChange={(e) => setLu(e.target.checked)} />J'ai recopié la clé sur la borne
      </label>
      <PiedFenetre><Bouton variante="primaire" Icone={Check} disabled={!lu} onClick={onFermer}>Terminé</Bouton></PiedFenetre>
    </Fenetre>
  );
}

function FenetreRevoquer({ borne, onFermer, onRevoquee }) {
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState('');
  return (
    <Fenetre titre={`Révoquer « ${borne.libelle} »`} Icone={Ban} onFermer={onFermer}>
      {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
      <p style={{ margin: '0 0 10px', fontSize: 13 }}>La borne n'enregistrera plus aucun passage. Les passages déjà enregistrés restent. Définitif.</p>
      <Champ libelle="Motif" requis aide="Par exemple : tablette perdue, clé divulguée, borne remplacée."><textarea className="rh-champ-saisie" rows={2} value={motif} onChange={(e) => setMotif(e.target.value)} /></Champ>
      <PiedFenetre>
        <Bouton onClick={onFermer}>Annuler</Bouton>
        <Bouton variante="danger" Icone={Ban} disabled={motif.trim().length < 3}
          onClick={async () => { try { await axios.patch(`${API}/${borne.id}/revoquer`, { motif }); onRevoquee(); } catch (e) { setErreur(messageErreur(e)); } }}>Révoquer</Bouton>
      </PiedFenetre>
    </Fenetre>
  );
}

export default function ReglagesBornesPage() {
  const droits = useDroitsTemps();
  const peut = !!droits?.pointage?.gerer_bornes;
  const [statut, setStatut] = useState('ACTIVE');
  const [bornes, setBornes] = useState(null);
  const [sites, setSites] = useState([]);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [fenetre, setFenetre] = useState(null);
  const charger = useCallback(async () => {
    setErreur('');
    try { setBornes((await axios.get(API, { params: { statut: statut || undefined } })).data.data); } catch (e) { setErreur(messageErreur(e, 'Chargement impossible.')); }
  }, [statut]);
  useEffect(() => { if (peut) charger(); }, [peut, charger]);
  useEffect(() => {
    if (!peut) return;
    if (droits.reglages?.lecture) axios.get('/api/temps/referentiels/options').then((r) => setSites(r.data.data.sites || [])).catch(() => {});
    else setSites((droits.rh?.sites || []).map((s) => ({ id: s.id, libelle: s.nom })));
  }, [peut, droits]);

  return (
    <Layout>
      <div className="rh-page">
        <EnTetePage fil={[{ libelle: 'Temps', vers: '/temps' }, { libelle: 'Réglages' }, { libelle: 'Bornes' }]} titre="Bornes de pointage"
          sousTitre="Une borne est une tablette de site : elle s'identifie par sa clé d'appareil, sans compte utilisateur."
          actions={peut && <Bouton variante="primaire" Icone={Plus} onClick={() => setFenetre({ genre: 'enroler' })}>Enrôler une borne</Bouton>} />
        <NavReglages actif="bornes" />
        {!droits && <Squelette largeur="50%" />}
        {droits && !peut && <EtatVide Icone={Tablet} titre="Accès non autorisé" texte="La gestion des bornes demande l'habilitation « Enrôler ou révoquer une borne de pointage »." />}
        {peut && (
          <>
            {erreur && <BandeauInfo ton="danger">{erreur}</BandeauInfo>}
            {message && <BandeauInfo ton="succes">{message}</BandeauInfo>}
            <div className="rh-outils">
              <div style={{ minWidth: 200 }}>
                <Selecteur libelle="État" valeur={statut} onChange={setStatut} tous="Toutes" options={[{ valeur: 'ACTIVE', libelle: 'En service' }, { valeur: 'REVOQUEE', libelle: 'Révoquées' }]} />
              </div>
            </div>
            <Carte titre="Bornes" Icone={Tablet}>
              {!bornes ? <Squelette largeur="60%" /> : !bornes.length ? <EtatVide Icone={Tablet} titre="Aucune borne" texte="Enrôlez la première borne d'un site." /> : (
                <div className="rh-conteneur-tableau">
                  <table className="rh-tableau">
                    <thead><tr><th>Borne</th><th>Site</th><th>État</th><th>Enrôlée</th><th>Dernière activité</th><th aria-label="Actions" /></tr></thead>
                    <tbody>
                      {bornes.map((b) => (
                        <tr key={b.id}>
                          <td className="rh-cellule-principale">{b.libelle}</td>
                          <td>{b.site_nom}</td>
                          <td>
                            <Badge ton={b.statut === 'ACTIVE' ? 'succes' : 'neutre'} point>{b.statut === 'ACTIVE' ? 'En service' : 'Révoquée'}</Badge>
                            {b.statut === 'REVOQUEE' && <span className="rh-cellule-secondaire" style={{ display: 'block' }}>le {dateHeure(b.revoquee_le)}{b.revoquee_par_nom ? ` par ${b.revoquee_par_nom}` : ''} · {b.motif_revocation}</span>}
                          </td>
                          <td>{dateHeure(b.enrolee_le)}{b.enrolee_par_nom ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>par {b.enrolee_par_nom}</span> : null}</td>
                          <td>{b.derniere_activite ? dateHeure(b.derniere_activite) : 'Jamais'}</td>
                          <td>{b.statut === 'ACTIVE' && <Bouton petit variante="discret" Icone={Ban} onClick={() => setFenetre({ genre: 'revoquer', borne: b })}>Révoquer</Bouton>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Carte>
          </>
        )}
        {fenetre?.genre === 'enroler' && <FenetreEnroler sites={sites} onFermer={() => setFenetre(null)} onEnrolee={(r) => { setFenetre({ genre: 'cle', resultat: r }); charger(); }} />}
        {fenetre?.genre === 'cle' && <FenetreCle resultat={fenetre.resultat} onFermer={() => { setFenetre(null); setMessage(`Borne « ${fenetre.resultat.borne.libelle} » enrôlée.`); }} />}
        {fenetre?.genre === 'revoquer' && <FenetreRevoquer borne={fenetre.borne} onFermer={() => setFenetre(null)} onRevoquee={() => { setFenetre(null); setMessage('Borne révoquée.'); charger(); }} />}
      </div>
    </Layout>
  );
}
